// EmptyCollectionGuide: Onboarding-Anleitung für Nutzer ohne Karten.
// Zeigt Schritt-für-Schritt-Anleitung zum ManaBox-Export plus direkte CSV-Upload-Möglichkeit.
import React, { useRef, useState, useCallback } from 'react';
import { Upload, Cloud, Loader2, CheckCircle2 } from 'lucide-react';
import { queryClient } from '../queryClient';
import { apiClient, json } from '../api/apiClient';
import { errorMessage } from '../api/errorMessage';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import screenshot1 from '../assets/ManaBox_Screenshot (1).jpg';
import screenshot2 from '../assets/ManaBox_Screenshot (2).jpg';
import ManaBoxTips from './ManaBoxTips';
import Divider from '../ui/Divider';
import { useToast } from '../ui/Toast';
import './EmptyCollectionGuide.css';

const STEPS = [
  {
    n: 1,
    img: screenshot1,
    annotation: { top: '4%', right: '1%' },
    label: <>Öffne deine Sammlung und tippe oben rechts auf <strong>⋯</strong></>,
  },
  {
    n: 2,
    img: screenshot2,
    annotation: { top: '11%', right: '23%' },
    label: <>Tippe auf <strong>„Exportieren"</strong></>,
  },
];

export default function EmptyCollectionGuide() {
  const fileInputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [driveLink, setDriveLink] = useState('');
  const [loadingDrive, setLoadingDrive] = useState(false);
  const { logout } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  // Hilfsfunktion: Extrahiert die Google Drive Datei-ID aus Link oder ID
  const extractDriveFileId = (value) => {
    if (!value) return '';
    const trimmed = String(value).trim();
    const matchPath = trimmed.match(/\/file\/d\/([^/]+)\//);
    if (matchPath && matchPath[1]) return matchPath[1];
    const matchQuery = trimmed.match(/[?&]id=([^&]+)/);
    if (matchQuery && matchQuery[1]) return matchQuery[1];
    return trimmed;
  };

  const handleDriveLoad = useCallback(async () => {
    if (!driveLink.trim() || loadingDrive) return;
    setLoadingDrive(true);

    const fileId = extractDriveFileId(driveLink);
    try {
      await apiClient.post('/api/load-from-drive', { fileId });

      setDriveLink('');
      queryClient.invalidateQueries({ queryKey: ['cards'] });
      // Kein setSuccess(true) hier: der Erfolgs-Zustand der Upload-Zone gilt nur für Datei-Uploads; Drive-Import meldet via Toast.
      toast.push({ tone: 'success', message: 'Sammlung importiert' });
    } catch (err) {
      // Fehlende Drive-Freigabe: der Server liefert die Google-Zustimmungs-Adresse mit;
      // die Datei-ID überdauert den Umweg über Google in der sessionStorage.
      if (err.code === 'DRIVE_AUTH_REQUIRED' && err.details?.authUrl) {
        sessionStorage.setItem('pendingDriveFileId', fileId);
        window.location.href = err.details.authUrl;
        return;
      }
      if (err.status === 401) {
        logout();
        navigate('/auth', { replace: true });
        return;
      }
      console.error('[EmptyCollectionGuide] Fehler beim Laden von Google Drive:', err);
      toast.push({ tone: 'error', message: errorMessage(err, 'Import fehlgeschlagen.') });
    } finally {
      setLoadingDrive(false);
    }
  }, [driveLink, loadingDrive, logout, navigate, toast]);

  const handleUpload = useCallback(async (file) => {
    if (!file || uploading) return;
    setSuccess(false);
    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      // FormData setzt seinen Content-Type mit Boundary selbst — deshalb json() statt apiClient.post
      await json('/api/upload-csv', { method: 'POST', body: formData });

      setSuccess(true);
      queryClient.invalidateQueries({ queryKey: ['cards'] });
      toast.push({ tone: 'success', message: 'Sammlung importiert' });
    } catch (err) {
      if (err.status === 401) {
        logout();
        navigate('/auth', { replace: true });
        return;
      }
      console.error('[EmptyCollectionGuide] Fehler beim CSV-Upload:', err);
      toast.push({ tone: 'error', message: errorMessage(err, 'Import fehlgeschlagen.') });
    } finally {
      setUploading(false);
    }
  }, [uploading, logout, navigate, toast]);

  const onFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) handleUpload(file);
  };

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file && (file.name.endsWith('.csv') || file.type === 'text/csv')) {
      handleUpload(file);
    } else {
      toast.push({ tone: 'error', message: 'Bitte eine .csv-Datei auswählen.' });
    }
  }, [handleUpload, toast]);

  const onDragOver = (e) => { e.preventDefault(); setDragOver(true); };
  const onDragLeave = () => setDragOver(false);

  return (
    <div className="empty-guide">
      {/* ── Header ───────────────────────────────── */}
      <header className="empty-guide__header">
        <h1>So importierst du deine Sammlung</h1>
        <p>
          Exportiere deine Karten aus <strong>ManaBox</strong> und lade sie hier hoch.
        </p>
      </header>

      {/* ── Schritt-Reihe: 2 Screenshots + Upload-Card ─── */}
      <div className="empty-guide__steps">
        {STEPS.map((step) => (
          <div key={step.n} className="empty-guide__step">
            <span className="empty-guide__badge">{step.n}</span>
            <div className="empty-guide__shot">
              <img src={step.img} alt={`ManaBox – Schritt ${step.n}`} />
              <span className="empty-guide__pulse" style={step.annotation} />
            </div>
            <p className="empty-guide__step-label">{step.label}</p>
          </div>
        ))}

        {/* Upload-Card — doppelt breit */}
        <div className="empty-guide__step empty-guide__step--upload">
          <span className="empty-guide__badge">3</span>

          {/* Drag-&-Drop-Zone */}
          <div
            className={
              'empty-guide__upload' +
              (dragOver ? ' empty-guide__upload--drag-over' : '') +
              (uploading ? ' empty-guide__upload--loading' : '')
            }
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            onClick={() => fileInputRef.current?.click()}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click(); }}
          >
            {uploading ? (
              <Loader2 size={40} className="empty-guide__upload-icon empty-guide__spinner" />
            ) : success ? (
              <CheckCircle2 size={40} style={{ color: 'var(--success)' }} />
            ) : (
              <Upload size={40} className="empty-guide__upload-icon" />
            )}

            <h3 className="empty-guide__upload-title">
              {uploading ? 'Wird importiert…' : success ? 'Import erfolgreich!' : 'CSV-Datei hochladen'}
            </h3>
            <p className="empty-guide__upload-text">
              {uploading
                ? 'Deine Sammlung wird verarbeitet. Das kann einen Moment dauern.'
                : success
                  ? 'Deine Sammlung wird gleich angezeigt.'
                  : 'Ziehe deine exportierte ManaBox-CSV hierher oder klicke, um sie auszuwählen.'}
            </p>

            {!uploading && !success && (
              <button
                type="button"
                className="btn-primary"
                onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
              >
                <Upload size={16} />
                Datei auswählen
              </button>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              onChange={onFileChange}
            />

            {!uploading && !success && (
              <p className="empty-guide__upload-hint">Akzeptiert: <strong>.csv</strong> (ManaBox-Export)</p>
            )}

            {/* Google Drive — innerhalb der Upload-Card */}
            <div className="empty-guide__drive">
              <Divider>oder per Google Drive</Divider>
              <div className="empty-guide__drive-row">
                <input
                  type="text"
                  value={driveLink}
                  onChange={(e) => setDriveLink(e.target.value)}
                  placeholder="Google-Drive-Link einfügen…"
                  onKeyDown={(e) => { e.stopPropagation(); if (e.key === 'Enter') handleDriveLoad(); }}
                  onClick={(e) => e.stopPropagation()}
                />
                <button
                  type="button"
                  className="btn-secondary"
                  disabled={!driveLink.trim() || loadingDrive}
                  onClick={(e) => { e.stopPropagation(); handleDriveLoad(); }}
                >
                  {loadingDrive
                    ? <><Loader2 size={14} className="empty-guide__spinner" /> Lädt…</>
                    : <><Cloud size={16} /> Importieren</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Tipps ──────────────────────────────────── */}
      <ManaBoxTips />
    </div>
  );
}
